import {
  Component,
  OnInit,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  colombianPhoneValidator,
  emailFormatValidator
} from '@view/shared/validators/form-validators';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AddSampleBaseComponent,
  SampleFormValue
} from '../../shared/base/add-sample-base.component';
import { InputTextComponent } from '@view/shared/molecules/input-text/input-text.component';
import { InputTextAreaComponent } from '@view/shared/molecules/input-text-area/input-text-area.component';
import { FormDropdownComponent } from '@view/shared/molecules/form-dropdown/form-dropdown.component';
import { FormDateComponent } from '@view/shared/molecules/form-date/form-date.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { ManualEntryPayload } from '@model/patient/sample-entry.model';
import { FamilyMemberModalComponent } from './family-member-modal/family-member-modal.component';
import {
  KinshipType,
  FamilyMemberData
} from '@infra-services/exam/exam-type.interface';

@Component({
  selector: 'app-add-sample-manual',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextComponent,
    InputTextAreaComponent,
    FormDropdownComponent,
    FormDateComponent,
    ButtonComponent,
    FamilyMemberModalComponent
  ],
  templateUrl: './add-sample-manual.component.html',
  styleUrls: ['../../shared/styles/add-sample.component.scss']
})
export class AddSampleManualComponent
  extends AddSampleBaseComponent
  implements OnInit
{
  @ViewChild('familyMemberModal')
  familyMemberModal!: FamilyMemberModalComponent;

  @Input() familyMemberMode = false;

  @Input() familyMemberKinshipType: KinshipType | null = null;

  isModalOpen = signal(false);

  @Output() familyMemberSaved = new EventEmitter<FamilyMemberData>();

  @Output() modalClosed = new EventEmitter<void>();

  override ngOnInit(): void {
    super.ngOnInit();
    this.configureManualMode();
  }

  private configureManualMode(): void {
    this.showForm.set(true);

    this.sampleForm.get('idNumber')?.enable();

    this.sampleForm.get('examCode')?.clearValidators();
    this.sampleForm.get('examCode')?.updateValueAndValidity();
    this.sampleForm.get('admissionDate')?.clearValidators();
    this.sampleForm.get('admissionDate')?.updateValueAndValidity();

    this.sampleForm
      .get('examType')
      ?.valueChanges.subscribe((examId: string) => {
        this.onExamTypeChange(examId);
      });

    const requiredFields = [
      'identifierType',
      'idNumber',
      'givenName',
      'familyName',
      'administrativeSex',
      'birthDate',
      'maritalStatus',
      'telephoneNumber',
      'specimenType',
      'specimenContainerId',
      'priorityId',
      'examType',
      'companyId',
      'providerId',
      'region',
      'site',
      'placerOrderNumber',
      'caseNumberInput',
      'laboratoriesId',
      'diagnosesId'
    ];

    requiredFields.forEach((field) => {
      const control = this.sampleForm.get(field);
      if (control) {
        if (field === 'telephoneNumber') {
          control.setValidators([Validators.required, colombianPhoneValidator()]);
        } else {
          control.setValidators([Validators.required]);
        }
        control.updateValueAndValidity();
      }
    });

    const optionalFields = [
      'emailAddress',
      'specialistsId',
      'middleName',
      'maternalFamilyName',
      'userDataDescription',
      'descriptionDataSample',
      'descriptionDataExam',
      'clinicalHistory'
    ];
    optionalFields.forEach((field) => {
      const control = this.sampleForm.get(field);
      if (control) {
        if (field === 'emailAddress') {
          control.setValidators([emailFormatValidator()]);
        } else {
          control.clearValidators();
        }
        control.updateValueAndValidity();
      }
    });
  }

  private onExamTypeChange(examId: string): void {
    if (!examId) {
      this.examTypeInfo.set(null);
      this.familyMembers.set([]);
      return;
    }

    this.checkExamTypeForFamilyMembers(examId);
  }

  openFamilyMemberModal(): void {
    if (this.familyMemberModal) {
      this.familyMemberModal.open();
    }
  }

  onFamilyMemberAdded(familyMember: FamilyMemberData): void {
    this.addFamilyMember(familyMember);
  }

  canSave(): boolean {
    if (this.sampleForm.invalid) {
      return false;
    }
    return this.canSaveWithFamilyMembers();
  }

  saveManualSample(): void {
    if (this.sampleForm.invalid) {
      this.showFormValidationError();
      return;
    }

    if (this.requiresFamilyMembers() && !this.canSave()) {
      this.toastService.setToast({
        type: ToastTypeEnum.ERROR,
        title: 'Faltan datos de familiares',
        message: this.getExamTypeMessage()
      });
      return;
    }

    const payload = this.buildManualEntryPayload();

    if (this.familyMembers().length > 0) {
      (
        payload as ManualEntryPayload & { familiares?: FamilyMemberData[] }
      ).familiares = this.familyMembers();
    }

    this.presenter.saveManualSample(payload).subscribe({
      next: () => this.onManualSaveSuccess(),
      error: (error: HttpErrorResponse) => this.onManualSaveError(error)
    });
  }

  private showFormValidationError(): void {
    Object.keys(this.sampleForm.controls).forEach((key) => {
      const control = this.sampleForm.get(key);
      control?.markAsTouched();
    });

    this.toastService.setToast({
      type: ToastTypeEnum.ERROR,
      title: 'Formulario incompleto',
      message:
        'Por favor complete todos los campos obligatorios antes de guardar.'
    });
  }

  private buildManualEntryPayload(): ManualEntryPayload {
    const formValue = this.sampleForm.getRawValue() as SampleFormValue;
    const birthDate = this.formatBirthDate(formValue.birthDate);
    const specimenId = formValue.specimenId || `SPEC-${Date.now()}`;

    const payload: ManualEntryPayload = {
      datosPaciente: {
        idNumber: formValue.idNumber,
        identifierTypeCode: formValue.identifierType,
        patientNumber: formValue.patientNumber,
        givenName: formValue.givenName,
        middleName: formValue.middleName || '',
        familyName: formValue.familyName,
        maternalFamilyName: formValue.maternalFamilyName || '',
        emailAddress: formValue.emailAddress || '',
        birthDate,
        administrativeSex: formValue.administrativeSex,
        maritalStatus: formValue.maritalStatus,
        race: formValue.race,
        telephoneNumber: formValue.telephoneNumber,
        streetAddress: formValue.streetAddress,
        city: formValue.city,
        state: formValue.state,
        country: formValue.birthCountry
      },
      datosMuestra: {
        specimenId,
        specimenTypeId: formValue.specimenType,
        specimenContainerId: formValue.specimenContainerId,
        priorityId: formValue.priorityId,
        reprocess: formValue.isReprocess
      },
      datosExamen: {
        examId: formValue.examType
      },
      datosOrden: {
        companyId: formValue.companyId,
        providerId: formValue.providerId,
        regionalId: formValue.region,
        siteId: formValue.site,
        placerOrderNumber: formValue.placerOrderNumber,
        caseNumber: formValue.caseNumberInput,
        consentForUseOfSamples: formValue.consentForUseOfSamples || false,
        userDataDescription: formValue.userDataDescription || '',
        descriptionDataSample: formValue.descriptionDataSample || '',
        descriptionDataExam: formValue.descriptionDataExam || '',
        clinicalHistory: formValue.clinicalHistory || '',
        prioritization: formValue.prioritization || false,
        acmg: formValue.acmg || false,
        mlpa: formValue.mlpa || false,
        laboratoryId: formValue.laboratoriesId,
        diagnosisId: formValue.diagnosesId,
        specialistId: formValue.specialistsId || ''
      }
    };

    if (formValue.isReprocess) {
      return {
        ...payload,
        datosReproceso: {
          reprocessCauseId: formValue.causeReprocessId,
          reprocessStatusId: formValue.statusReprocessId,
          reprocessObservation: formValue.reprocessDescription
        }
      };
    }

    return payload;
  }

  private formatBirthDate(birthDate: string | undefined): string {
    if (!birthDate) {
      return '';
    }

    const dateObj = new Date(birthDate);
    return Number.isNaN(dateObj.getTime()) ? birthDate : dateObj.toISOString();
  }

  private onManualSaveSuccess(): void {
    this.toastService.setToast({
      title: 'Muestra guardada exitosamente',
      type: ToastTypeEnum.SUCCESS
    });
    setTimeout(() => {
      this.toastService.setToast(null);
    }, 10000);
  }

  private onManualSaveError(error: any): void {
    const backendMessage = this.extractBackendMessage(error);

    const fallbackMessages: Record<number, string> = {
      400: 'Datos inválidos. Por favor, verifique la información ingresada.',
      401: 'No autorizado. Por favor, inicie sesión nuevamente.',
      403: 'No tiene permisos para realizar esta acción.',
      500: 'Error interno del servidor. Por favor, contacte al administrador.'
    };

    const errorMessage =
      backendMessage || fallbackMessages[error.status] || 'Error al guardar la muestra';

    this.toastService.setToast({
      type: ToastTypeEnum.ERROR,
      title: 'Error al guardar',
      message: errorMessage
    });
  }

  identifierTypesForModal(): { id: string; name: string }[] {
    return this.identifierTypeDropdownOptions.map((opt) => ({
      id: opt.value,
      name: opt.label
    }));
  }

  openAsModal(kinshipType: KinshipType): void {
    this.familyMemberMode = true;
    this.familyMemberKinshipType = kinshipType;
    this.isModalOpen.set(true);

    if (
      kinshipType === KinshipType.PADRE ||
      kinshipType === KinshipType.FUTURO_PADRE
    ) {
      this.sampleForm.patchValue({ administrativeSex: 'M' });
    } else if (
      kinshipType === KinshipType.MADRE ||
      kinshipType === KinshipType.FUTURA_MADRE
    ) {
      this.sampleForm.patchValue({ administrativeSex: 'F' });
    }
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.familyMemberMode = false;
    this.familyMemberKinshipType = null;
    this.sampleForm.reset();
    this.modalClosed.emit();
  }

  saveFamilyMember(): void {
    if (this.sampleForm.invalid || !this.familyMemberKinshipType) {
      Object.keys(this.sampleForm.controls).forEach((key) => {
        this.sampleForm.get(key)?.markAsTouched();
      });
      return;
    }

    const formValue = this.sampleForm.getRawValue() as SampleFormValue;

    const familyMember: FamilyMemberData = {
      tipoParentesco: this.familyMemberKinshipType,
      datosPaciente: {
        idNumber: formValue.idNumber,
        identifierTypeCode: formValue.identifierType,
        givenName: formValue.givenName,
        middleName: formValue.middleName,
        familyName: formValue.familyName,
        maternalFamilyName: formValue.maternalFamilyName,
        emailAddress: formValue.emailAddress,
        birthDate: this.formatBirthDate(formValue.birthDate),
        administrativeSex: formValue.administrativeSex,
        maritalStatus: formValue.maritalStatus,
        race: formValue.race,
        telephoneNumber: formValue.telephoneNumber,
        streetAddress: formValue.streetAddress,
        city: formValue.city,
        state: formValue.state,
        country: formValue.birthCountry
      }
    };

    this.familyMemberSaved.emit(familyMember);
    this.closeModal();
  }

  getModalTitle(): string {
    if (!this.familyMemberKinshipType) {
      return 'Agregar Familiar';
    }

    const labels: Record<KinshipType, string> = {
      [KinshipType.PADRE]: 'Ingreso de Datos del Padre',
      [KinshipType.MADRE]: 'Ingreso de Datos de la Madre',
      [KinshipType.FUTURO_PADRE]: 'Ingreso de Datos del Futuro Padre',
      [KinshipType.FUTURA_MADRE]: 'Ingreso de Datos de la Futura Madre'
    };

    return labels[this.familyMemberKinshipType] || 'Agregar Familiar';
  }
}
